import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, InjectionToken, PLATFORM_ID } from '@angular/core';
import { LOCAL_STORAGE_CONSTANT } from '@constants/localstorage.constant';
import { CryptoService } from '@services/crypto/crypto.service';

@Injectable({
  providedIn: 'root',
})
export class LocalStorageService {
  constructor(
    @Inject(PLATFORM_ID) private platformId: InjectionToken<object>,
    @Inject('LOCALSTORAGE') private localStorage: Storage,
    private cryptoService: CryptoService,
  ) {}

  get(key: string) {
    if (isPlatformBrowser(this.platformId)) {
      const encryptedValue = this.localStorage.getItem(key);
      return (
        encryptedValue &&
        JSON.parse(this.cryptoService.decryptValue(encryptedValue))
      );
    }
  }

  set<T>(key: string, value: T) {
    if (isPlatformBrowser(this.platformId)) {
      const encryptedValue = this.cryptoService.encryptValue(
        JSON.stringify(value),
      );
      this.localStorage.setItem(key, encryptedValue);
    }
  }

  remove(key: string) {
    if (isPlatformBrowser(this.platformId)) {
      this.localStorage.removeItem(key);
    }
  }

  clear() {
    if (isPlatformBrowser(this.platformId)) {
      const persistentKeys = [
        LOCAL_STORAGE_CONSTANT.CURRENT_LANGUAGE_STATE_KEY,
        LOCAL_STORAGE_CONSTANT.PROMPT_BEEN_SHOWN,
        LOCAL_STORAGE_CONSTANT.REPORT_PERIOD,
        LOCAL_STORAGE_CONSTANT.SELECTED_USERS,
        LOCAL_STORAGE_CONSTANT.SELECTED_CUSTOMERS,
        LOCAL_STORAGE_CONSTANT.SELECTED_CUSTOMER_GROUPS,
        LOCAL_STORAGE_CONSTANT.SELECTED_VEHICLES,
        LOCAL_STORAGE_CONSTANT.SELECTED_SENDERS,
        LOCAL_STORAGE_CONSTANT.SELECTED_RECEIVERS,
        LOCAL_STORAGE_CONSTANT.SELECTED_PRODUCTS,
        LOCAL_STORAGE_CONSTANT.SELECTED_STATUS,
        LOCAL_STORAGE_CONSTANT.SELECTED_WORK_TYPES,
        LOCAL_STORAGE_CONSTANT.SELECTED_WORK_OBJECTS,
        LOCAL_STORAGE_CONSTANT.SELECTED_WORK_GROUPS,
        LOCAL_STORAGE_CONSTANT.SELECTED_PRODUCT_GROUPS,
        LOCAL_STORAGE_CONSTANT.SELECTED_BIOGAS_PRODUCTS,
      ];

      const persistentData: { [key: string]: any } = {};
      persistentKeys.forEach((key) => {
        const value = this.get(key);
        if (value) {
          persistentData[key] = value;
        }
      });

      this.localStorage.clear();
      window.sessionStorage.clear();

      Object.keys(persistentData).forEach((key) => {
        this.set(key, persistentData[key]);
      });
    }
  }
}
